import { Check, Pencil, Plus, Trash2 } from "lucide-react";
import { useState } from "react";
import { ParentLayout } from "@/components/layout/ParentLayout";
import { Button } from "@/components/ui/Button";
import { Card } from "@/components/ui/Card";
import { Checkbox } from "@/components/ui/Checkbox";
import { ConfirmDialog } from "@/components/ui/ConfirmDialog";
import { EmojiPicker } from "@/components/ui/EmojiPicker";
import { IconButton } from "@/components/ui/IconButton";
import { Pill } from "@/components/ui/Pill";
import { useObservable } from "@/hooks/useObservable";
import { formatKr } from "@/lib/format";
import { getCurrentProfile } from "@/lib/profile";
import { familyService } from "@/services/family.service";
import { jobsService } from "@/services/jobs.service";
import type { JobData, JobRecord } from "@/types/domain";

const BLANK: Omit<JobData, "sort_order"> = {
  emoji: "✅",
  title: "",
  price: 20,
  max_per_day: null,
  max_per_week: null,
  kind: "regular",
  bands: null,
  require_comment: false,
  comment_label: null,
  requires_approval: false,
  assigned_to: [],
  is_active: true,
};

export function ParentJobs() {
  const jobs = useObservable(jobsService.jobs$, []);
  const familyState = useObservable(familyService.state$, {
    members: [],
    settings: null,
    loading: false,
  });
  const children = familyState.members.filter((m) => m.data.role === "child");

  const [editing, setEditing] = useState<JobRecord | "new" | null>(null);
  const [form, setForm] = useState<Omit<JobData, "sort_order">>(BLANK);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [pendingDelete, setPendingDelete] = useState<JobRecord | null>(null);

  const regular = jobs.filter((j) => j.data.kind === "regular");

  function openNew() {
    setForm({ ...BLANK, assigned_to: children.map((c) => c.id) });
    setEditing("new");
  }

  function openEdit(job: JobRecord) {
    setForm({ ...BLANK, ...job.data });
    setEditing(job);
  }

  async function handleSave() {
    if (!form.title) {
      setFormError("Jobben må ha et navn.");
      return;
    }
    if (children.length > 0 && form.assigned_to.length === 0) {
      setFormError("Jobben må tildeles minst ett barn.");
      return;
    }
    if (form.require_comment && !form.comment_label?.trim()) {
      setFormError("Skriv en tittel på kommentaren.");
      return;
    }
    setFormError(null);
    if (saving) return;
    setSaving(true);
    try {
      const data: JobData = {
        ...form,
        sort_order:
          editing === "new"
            ? regular.length
            : (editing as JobRecord).data.sort_order,
      };
      if (editing === "new") {
        const familyId = getCurrentProfile().profile!.data.family_id;
        await jobsService.createJob(familyId, data);
      } else {
        await jobsService.updateJob((editing as JobRecord).id, data);
      }
      setEditing(null);
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete() {
    if (!pendingDelete) return;
    await jobsService.archiveJob(pendingDelete.id);
    setPendingDelete(null);
  }

  return (
    <>
      <ParentLayout>
        <div style={{ maxWidth: 680 }}>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-end",
              marginBottom: 28,
            }}
          >
            <div>
              <div
                style={{
                  fontSize: 12,
                  color: "var(--ink-3)",
                  textTransform: "uppercase",
                  letterSpacing: 0.7,
                  fontWeight: 700,
                }}
              >
                Administrasjon
              </div>
              <div
                style={{
                  fontFamily: "var(--font-display)",
                  fontStyle: "italic",
                  fontSize: 34,
                  fontWeight: 400,
                  marginTop: 2,
                  letterSpacing: -0.8,
                }}
              >
                Jobber
              </div>
            </div>
            <Button
              variant="primary"
              size="sm"
              icon={<Plus size={15} color="#fff" />}
              onClick={openNew}
              data-testid="new-job-button"
            >
              Ny jobb
            </Button>
          </div>

          {editing && (
            <Card
              style={{
                padding: 20,
                marginBottom: 20,
                border: "2px solid var(--forest)",
              }}
            >
              <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 16 }}>
                {editing === "new" ? "Ny jobb" : "Rediger jobb"}
              </div>

              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 22,
                }}
              >
                <Field label="Emoji">
                  <EmojiPicker
                    data-testid="job-emoji-picker"
                    value={form.emoji}
                    onChange={(emoji) => setForm((f) => ({ ...f, emoji }))}
                  />
                </Field>
                <Field label="Navn">
                  <input
                    data-testid="job-title-input"
                    value={form.title}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, title: e.target.value }))
                    }
                    style={inputStyle}
                    placeholder="f.eks. Rydde rom"
                  />
                </Field>
                <Field label="Pris">
                  <div
                    data-testid="price-mode-toggle"
                    style={{
                      display: "flex",
                      gap: 0,
                      border: "1px solid var(--line-2)",
                      borderRadius: 8,
                      padding: 2,
                      background: "var(--bg)",
                      marginBottom: form.price !== null ? 8 : 0,
                    }}
                  >
                    <PriceModeOption
                      testId="price-mode-fixed"
                      active={form.price !== null}
                      label="Fast pris"
                      onClick={() =>
                        setForm((f) =>
                          f.price !== null ? f : { ...f, price: 20 },
                        )
                      }
                    />
                    <PriceModeOption
                      testId="price-mode-child"
                      active={form.price === null}
                      label="Barnet foreslår"
                      onClick={() => setForm((f) => ({ ...f, price: null }))}
                    />
                  </div>
                  {form.price !== null && (
                    <input
                      data-testid="job-price-input"
                      type="number"
                      value={form.price}
                      onChange={(e) =>
                        setForm((f) => ({
                          ...f,
                          price:
                            e.target.value === "" ? 0 : Number(e.target.value),
                        }))
                      }
                      style={inputStyle}
                      placeholder="kr"
                    />
                  )}
                </Field>
                <Field
                  label="Kan gjøres x ganger per dag"
                  hint="La stå tom for ingen begrensning"
                >
                  <input
                    data-testid="job-max-per-day-input"
                    type="number"
                    value={form.max_per_day ?? ""}
                    onChange={(e) =>
                      setForm((f) => ({
                        ...f,
                        max_per_day:
                          e.target.value === "" ? null : Number(e.target.value),
                      }))
                    }
                    style={inputStyle}
                  />
                </Field>
                <Field
                  label="Kan gjøres x ganger per uke"
                  hint="La stå tom for ingen begrensning"
                >
                  <input
                    data-testid="job-max-per-week-input"
                    type="number"
                    value={form.max_per_week ?? ""}
                    onChange={(e) =>
                      setForm((f) => ({
                        ...f,
                        max_per_week:
                          e.target.value === "" ? null : Number(e.target.value),
                      }))
                    }
                    style={inputStyle}
                  />
                </Field>
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 8,
                  }}
                >
                  <Checkbox
                    data-testid="require-comment-checkbox"
                    checked={form.require_comment}
                    onChange={(checked) =>
                      setForm((f) => ({ ...f, require_comment: checked }))
                    }
                  >
                    Krever kommentar
                  </Checkbox>
                  {form.require_comment && (
                    <div style={{ marginLeft: 28, marginTop: 4 }}>
                      <input
                        data-testid="comment-label-input"
                        value={form.comment_label ?? ""}
                        onChange={(e) =>
                          setForm((f) => ({
                            ...f,
                            comment_label: e.target.value || null,
                          }))
                        }
                        placeholder='Tittel på kommentaren (f.eks. "Hva gjorde du?")'
                        style={inputStyle}
                      />
                    </div>
                  )}
                  <Checkbox
                    data-testid="requires-approval-checkbox"
                    checked={form.requires_approval}
                    onChange={(checked) =>
                      setForm((f) => ({ ...f, requires_approval: checked }))
                    }
                  >
                    Krever godkjenning av foreldre
                  </Checkbox>
                </div>
              </div>

              {children.length > 0 && (
                <Field label="Kan utføres av" style={{ marginTop: 22 }}>
                  <div
                    style={{
                      display: "flex",
                      gap: 8,
                      flexWrap: "wrap",
                      marginTop: 4,
                    }}
                  >
                    {children.map((c) => (
                      <Checkbox
                        key={c.id}
                        data-testid="child-checkbox"
                        checked={form.assigned_to.includes(c.id)}
                        onChange={(checked) =>
                          setForm((f) => ({
                            ...f,
                            assigned_to: checked
                              ? [...f.assigned_to, c.id]
                              : f.assigned_to.filter((id) => id !== c.id),
                          }))
                        }
                      >
                        {c.data.name}
                      </Checkbox>
                    ))}
                  </div>
                </Field>
              )}

              {formError && (
                <div
                  data-testid="form-error"
                  style={{ color: "var(--bad)", fontSize: 13, marginTop: 12 }}
                >
                  {formError}
                </div>
              )}
              <div style={{ display: "flex", gap: 8, marginTop: 28 }}>
                <Button
                  variant="primary"
                  size="sm"
                  icon={<Check size={14} color="#fff" />}
                  onClick={handleSave}
                  disabled={saving}
                  data-testid="save-job-button"
                >
                  {saving ? "Lagrer…" : "Lagre"}
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setEditing(null)}
                >
                  Avbryt
                </Button>
              </div>
            </Card>
          )}

          {regular.length > 0 && (
            <Card padding={0} style={{ overflow: "hidden" }}>
              {regular.map((j, i) => (
                <div
                  key={j.id}
                  data-testid="job-item"
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 12,
                    padding: "14px 16px",
                    borderBottom:
                      i < regular.length - 1 ? "1px solid var(--line)" : "none",
                  }}
                >
                  <div style={{ fontSize: 22, width: 36, textAlign: "center" }}>
                    {j.data.emoji}
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 600, fontSize: 14 }}>
                      {j.data.title}
                    </div>
                    <div
                      className="num"
                      style={{ fontSize: 12, color: "var(--ink-3)" }}
                    >
                      {j.data.price !== null
                        ? formatKr(j.data.price)
                        : "Barnet foreslår pris"}
                      {j.data.max_per_day &&
                        ` · maks ${j.data.max_per_day}/dag`}
                      {j.data.max_per_week &&
                        ` · maks ${j.data.max_per_week}/uke`}
                      {j.data.require_comment && " · krever kommentar"}
                      {j.data.requires_approval && " · krever godkjenning"}
                    </div>
                  </div>
                  {children
                    .filter((c) => j.data.assigned_to.includes(c.id))
                    .map((c) => (
                      <div
                        key={c.id}
                        data-testid="child-avatar"
                        title={c.data.name}
                        style={{
                          width: 24,
                          height: 24,
                          borderRadius: "50%",
                          background: c.data.color,
                          color: "#fff",
                          fontSize: 10,
                          fontWeight: 700,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          flexShrink: 0,
                        }}
                      >
                        {c.data.initials}
                      </div>
                    ))}
                  {!j.data.is_active && (
                    <Pill tone="warn" size="sm">
                      Inaktiv
                    </Pill>
                  )}
                  <div style={{ display: "flex", gap: 2 }}>
                    <IconButton
                      data-testid="edit-job-button"
                      onClick={() => openEdit(j)}
                    >
                      <Pencil size={24} color="currentColor" />
                    </IconButton>
                    <IconButton
                      data-testid="delete-job-button"
                      tone="danger"
                      onClick={() => setPendingDelete(j)}
                    >
                      <Trash2 size={24} color="currentColor" />
                    </IconButton>
                  </div>
                </div>
              ))}
            </Card>
          )}
        </div>
      </ParentLayout>
      {pendingDelete && (
        <ConfirmDialog
          title="Slett jobb?"
          message={`«${pendingDelete.data.title}» blir slettet permanent.`}
          onConfirm={handleDelete}
          onCancel={() => setPendingDelete(null)}
        />
      )}
    </>
  );
}

function Field({
  label,
  hint,
  children,
  style,
}: {
  label: string;
  hint?: string;
  children: React.ReactNode;
  style?: React.CSSProperties;
}) {
  return (
    <div style={style}>
      {label && (
        <div
          style={{
            fontSize: 12,
            fontWeight: 700,
            textTransform: "uppercase",
            letterSpacing: 0.6,
            color: "var(--ink)",
            marginBottom: 8,
          }}
        >
          {label}
        </div>
      )}
      {children}
      {hint && (
        <div
          style={{
            fontSize: 12,
            color: "var(--ink-3)",
            marginTop: 6,
          }}
        >
          {hint}
        </div>
      )}
    </div>
  );
}

function PriceModeOption({
  testId,
  active,
  label,
  onClick,
}: {
  testId: string;
  active: boolean;
  label: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      data-testid={testId}
      data-active={active ? "true" : "false"}
      onClick={onClick}
      style={{
        flex: 1,
        height: 32,
        borderRadius: 6,
        border: "none",
        background: active ? "var(--forest)" : "transparent",
        color: active ? "#fff" : "var(--ink-3)",
        fontSize: 13,
        fontWeight: 600,
        cursor: "pointer",
      }}
    >
      {label}
    </button>
  );
}

const inputStyle: React.CSSProperties = {
  width: "100%",
  height: 38,
  padding: "0 10px",
  border: "1px solid var(--line-2)",
  borderRadius: 8,
  fontSize: 14,
  background: "var(--bg)",
  boxSizing: "border-box",
};
